import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { 
  ArrowLeft,
  Search, 
  Filter, 
  RefreshCw,
  Plus,
  Edit,
  Eye,
  Trash2,
  FileText,
  Calendar,
  DollarSign,
  Users,
  Clock,
  Loader2,
  AlertCircle,
  CheckCircle,
} from 'lucide-react'
import apiService from '../../services/api'
import { toast } from 'react-toastify'

interface MyRfq {
  id: string
  title: string
  description: string
  budgetRange: string
  location: string
  deadline: string
  status: 'draft' | 'published' | 'closed' | 'awarded'
  solicitationNumber: string
  naicsCode: string
  setAside: string
  contractType: string
  createdAt: string
  updatedAt: string
  proposalCount: number
  _count?: {
    GovProposals: number
  }
}

const MyRfqsPage: React.FC = () => {
  const navigate = useNavigate()
  const [rfqs, setRfqs] = useState<MyRfq[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchMyRfqs()
  }, [])

  const fetchMyRfqs = async () => {
    try {
      setLoading(true)
      setError(null)
      
      console.log('MyRfqsPage - Fetching RFQs from /api/gov/rfqs/my')
      const response = await apiService.get('/api/gov/rfqs/my') as any
      console.log('MyRfqsPage - API Response:', response)
      
      if (response?.success) {
        // The API returns { data: { rfqs: [...], pagination: {...} } }
        const rfqsData = response.data?.rfqs || []
        setRfqs(rfqsData)
        console.log('MyRfqsPage - Set RFQs:', rfqsData)
      } else {
        console.error('MyRfqsPage - API returned error:', response)
        const errorMessage = response?.message || 'Failed to fetch your RFQs'
        setError(errorMessage)
        toast.error(errorMessage)
      }
    } catch (err: any) {
      console.error('MyRfqsPage - Error fetching my RFQs:', err)
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch your RFQs'
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const filteredRfqs = (rfqs || []).filter(rfq => {
    const matchesSearch = rfq.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         rfq.solicitationNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         rfq.description?.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesFilter = statusFilter === 'all' || rfq.status === statusFilter
    
    return matchesSearch && matchesFilter
  })

  const getStatusBadge = (status: string) => {
    // Handle undefined or null status
    const safeStatus = status || 'draft'
    
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200', icon: FileText },
      published: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200', icon: CheckCircle },
      closed: { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200', icon: Clock },
      awarded: { color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', icon: CheckCircle }
    }
    
    const config = statusConfig[safeStatus as keyof typeof statusConfig] || statusConfig.draft
    const Icon = config.icon
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {safeStatus.charAt(0).toUpperCase() + safeStatus.slice(1)}
      </span>
    )
  }

  const handleDeleteRfq = async (rfqId: string) => {
    if (!confirm('Are you sure you want to delete this RFQ? This action cannot be undone.')) {
      return
    }

    try {
      const response = await apiService.delete(`/api/gov/rfqs/${rfqId}`) as any
      
      if (response?.success) {
        toast.success('RFQ deleted successfully')
        fetchMyRfqs()
      } else {
        toast.error('Failed to delete RFQ')
      }
    } catch (err) {
      console.error('Error deleting RFQ:', err)
      toast.error('Failed to delete RFQ')
    }
  }

  const RfqCard: React.FC<{ rfq: MyRfq }> = ({ rfq }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {rfq.title}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
            {rfq.description}
          </p>
        </div>
        {getStatusBadge(rfq.status)}
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
          <FileText className="w-4 h-4 mr-2" />
          {rfq.solicitationNumber}
        </div>
        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
          <DollarSign className="w-4 h-4 mr-2" />
          {rfq.budgetRange}
        </div>
        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
          <Calendar className="w-4 h-4 mr-2" />
          {new Date(rfq.deadline).toLocaleDateString()}
        </div>
        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
          <Users className="w-4 h-4 mr-2" />
          {rfq.proposalCount || rfq._count?.GovProposals || 0} proposals
        </div>
      </div>
      
      <div className="flex items-center justify-between pt-4 border-t dark:border-gray-700">
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Created {new Date(rfq.createdAt).toLocaleDateString()}
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => navigate(`/gov/rfqs/${rfq.id}`)}
            className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium"
          >
            <Eye className="w-4 h-4 inline mr-1" />
            View
          </button>
          
          {rfq.status === 'draft' && (
            <button
              onClick={() => navigate(`/gov/rfqs/${rfq.id}/edit`)}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm font-medium"
            >
              <Edit className="w-4 h-4 inline mr-1" />
              Edit
            </button>
          )}
          
          {rfq.status === 'draft' && (
            <button
              onClick={() => handleDeleteRfq(rfq.id)}
              className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 text-sm font-medium"
            >
              <Trash2 className="w-4 h-4 inline mr-1" />
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4 mb-6">
            <Link 
              to="/gov/dashboard"
              className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Dashboard
            </Link>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                My RFQs
              </h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Manage your government Request for Quotations
              </p>
            </div>
            
            <div className="mt-4 lg:mt-0 flex items-center space-x-4">
              <button
                onClick={fetchMyRfqs}
                disabled={loading}
                className="btn btn-secondary flex items-center space-x-2"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
              
              <Link
                to="/gov/rfqs/create"
                className="btn btn-primary flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Create RFQ</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6 mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search RFQs by title, solicitation number, or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10"
                />
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="input w-auto"
                >
                  <option value="all">All Status</option>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="closed">Closed</option>
                  <option value="awarded">Awarded</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="mb-6">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Showing {filteredRfqs.length} of {rfqs.length} RFQs
          </p>
        </div>

        {/* RFQ Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="flex flex-col items-center">
              <Loader2 className="w-8 h-8 text-primary-600 animate-spin mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Loading your RFQs...</p>
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="flex flex-col items-center">
              <AlertCircle className="w-8 h-8 text-red-500 mb-4" />
              <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
              <button
                onClick={fetchMyRfqs}
                className="btn btn-primary"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : filteredRfqs.length === 0 ? (
          <div className="text-center py-12">
            <div className="flex flex-col items-center">
              <FileText className="w-8 h-8 text-gray-400 mb-4" />
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {searchTerm || statusFilter !== 'all' 
                  ? 'No RFQs match your search criteria.' 
                  : 'You haven\'t created any RFQs yet.'}
              </p>
              {!searchTerm && statusFilter === 'all' && (
                <Link
                  to="/gov/rfqs/create"
                  className="btn btn-primary"
                >
                  Create Your First RFQ
                </Link>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredRfqs.map((rfq) => (
              <RfqCard key={rfq.id} rfq={rfq} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default MyRfqsPage